import { useCallback, useEffect, useMemo, useState } from 'react';

const PLATFORM = 'instagram';
const storageKey = owner => `ginastee:studio:account:v3:${owner}`;
const readChoice = owner => {
  if (!owner) return null;
  try { return window.localStorage.getItem(storageKey(owner)); } catch { return null; }
};
export const profileKey = profile => profile.id;

// The creator's Instagram profiles and the one the Studio is working in (contract 2.0.0):
// every profile is a separate workspace — its own projects, memories, topics and voice —
// and none is primary. The choice is this browser's (by profile id); without one, or
// when it is gone, the first connected profile. Another one is connected through the
// same @ onboarding (`onboarding.startAdding`) while `features.multiple_profiles` is on
// and `limits.max_profiles` allows.
export default function useAccounts({ owner, creator, features, limits }) {
  const [choice, setChoice] = useState(() => readChoice(owner));
  useEffect(() => { setChoice(readChoice(owner)); }, [owner]);
  const profiles = creator?.profiles;
  const list = useMemo(() => (profiles || []).filter(profile => profile.platform === PLATFORM), [profiles]);
  const active = list.find(profile => profile.id === choice) || list[0] || null;
  const canAdd = list.length > 0 && !!features.multiple_profiles && list.length < (limits?.max_profiles ?? Infinity);

  const select = useCallback(profile => {
    const id = typeof profile === 'string' ? profile : profile?.id;
    if (!id) return;
    setChoice(id);
    try { window.localStorage.setItem(storageKey(owner), id); } catch { /* Kept for this visit only. */ }
  }, [owner]);

  // One identity while nothing changed: the header is memoized on `accounts`.
  return useMemo(() => ({ list, active, canAdd, select }), [list, active, canAdd, select]);
}
